import React, { useState, useEffect } from "react";
import api from "../../utils/axiosInstance";
import AdminSideMenu from "../../components/AdminSideMenu";

const ReportedIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchReportedIssues();
  }, []);

  const fetchReportedIssues = async () => {
    try {
      setLoading(true);
      const response = await api.get("/problem-reports");

      console.log("API Response:", response.data);

      // Handle different response structures
      if (response.data.message === "No problem reports found") {
        setIssues([]);
      } else if (Array.isArray(response.data)) {
        setIssues(response.data);
      } else if (Array.isArray(response.data.data)) {
        setIssues(response.data.data);
      } else if (response.data.debug?.sampleReports) {
        setIssues(response.data.debug.sampleReports);
      } else {
        setIssues([]);
      }

      setError("");
    } catch (err) {
      console.error("Error fetching reported issues:", err);
      setError("Failed to fetch reported issues. Please try again.");
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleActionInitiated = async (id) => {
    try {
      await api.put(`/problem-reports/${id}/status`, {
        status: "in_progress",
        note: "Action has been initiated",
      });
      window.alert("Action initiated successfully!");
      fetchReportedIssues();
    } catch (err) {
      console.error("Error updating status:", err);
      window.alert("Failed to update status. Please try again.");
    }
  };

  const handleProblemFixed = async (id) => {
    try {
      await api.put(`/problem-reports/${id}/status`, {
        status: "resolved",
        note: "Problem has been fixed",
      });
      window.alert("Problem marked as fixed!");
      fetchReportedIssues();
    } catch (err) {
      console.error("Error updating status:", err);
      window.alert("Failed to update status. Please try again.");
    }
  };

  // Download image function
  const downloadImage = async (imageUrl, fileName) => {
    try {
      const baseURL = process.env.REACT_APP_IMAGE_BASE_URL || "http://localhost:5000";

      // Handle both relative and absolute URLs
      let fullUrl = imageUrl;
      if (!imageUrl.startsWith("http")) {
        fullUrl = `${baseURL}/${imageUrl}`;
      }

      // Fetch the image
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "attachment.png";
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      console.log("Image downloaded successfully");
    } catch (error) {
      console.error("Error downloading image:", error);
      window.alert("Failed to download image. Please try again.");
    }
  };

  // Get attachment info
  const getAttachmentInfo = (issue) => {
    if (issue.attachment) {
      return {
        hasAttachment: true,
        url: issue.attachment.path,
        filename: issue.attachment.filename,
        isImage: issue.attachment.mimetype?.startsWith("image/"),
      };
    } else if (issue.images && issue.images.length > 0) {
      return {
        hasAttachment: true,
        url: issue.images[0].url,
        filename: issue.images[0].filename,
        isImage: true,
      };
    }
    return { hasAttachment: false };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      open: "bg-yellow-100 text-yellow-800",
      in_progress: "bg-blue-100 text-blue-800",
      resolved: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`px-3 py-1 text-xs font-medium rounded-full ${
          statusMap[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  const getSegmentBadge = (segment) => {
    const segmentMap = {
      pickup: "bg-purple-100 text-purple-800",
      transit: "bg-indigo-100 text-indigo-800",
      delivery: "bg-pink-100 text-pink-800",
      billing: "bg-red-100 text-red-800",
      other: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`px-3 py-1 text-xs font-medium rounded-full ${
          segmentMap[segment] || "bg-gray-100 text-gray-800"
        }`}
      >
        {segment.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="flex bg-light-gray min-h-screen">
      <AdminSideMenu />
      <div className="flex-1 sm:ml-64 p-4 sm:p-8">
        <div className="">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Reported Issues
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                View and manage all reported issues from carriers
              </p>
            </div>
            <button
              onClick={fetchReportedIssues}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition"
            >
              <i className="fas fa-sync-alt"></i>
              Refresh
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle text-red-500 text-xl mr-3"></i>
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
              <p className="text-gray-600 font-medium">Loading issues...</p>
            </div>
          ) : issues.length === 0 ? (
            /* Empty State */
            <div className="bg-white rounded-lg shadow-sm p-16 text-center">
              <div className="max-w-md mx-auto">
                <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-clipboard-list text-gray-400 text-5xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  No Issue Reports Found
                </h3>
                <p className="text-gray-600 text-lg mb-6">
                  There are currently no reported issues from carriers.
                </p>
                <p className="text-gray-500 text-sm">
                  When carriers report problems, they will appear here for your
                  review and action.
                </p>
              </div>
            </div>
          ) : (
            /* Issues Table */
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Issue Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Carrier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {issues.map((issue) => {
                      const attachmentInfo = getAttachmentInfo(issue);

                      return (
                        <tr
                          key={issue._id}
                          className="hover:bg-gray-50 transition"
                        >
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                {issue.title}
                              </p>
                              <p className="text-sm text-gray-500 line-clamp-2">
                                {issue.description || issue.problemDescription}
                              </p>
                              {attachmentInfo.hasAttachment && (
                                <div className="mt-2 flex items-center gap-2">
                                  <span className="inline-flex items-center text-xs text-blue-600">
                                    <i className="fas fa-paperclip mr-1"></i>
                                    {attachmentInfo.isImage ? "Image" : "File"}
                                  </span>
                                  {attachmentInfo.isImage && (
                                    <button
                                      onClick={() =>
                                        downloadImage(
                                          attachmentInfo.url,
                                          attachmentInfo.filename
                                        )
                                      }
                                      className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium transition"
                                      title="Download attachment"
                                    >
                                      <i className="fas fa-download mr-1"></i>
                                      Download
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {issue.carrierName ||
                                  issue.carrierId?.companyName ||
                                  "N/A"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {issue.carrierCode ||
                                  issue.carrierId?.identificationNumber ||
                                  "N/A"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {getSegmentBadge(issue.segment)}
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(issue.status)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(issue.createdAt)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {issue.status !== "in_progress" &&
                                issue.status !== "resolved" && (
                                  <button
                                    onClick={() =>
                                      handleActionInitiated(issue._id)
                                    }
                                    className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700 transition whitespace-nowrap"
                                  >
                                    <i className="fas fa-play-circle mr-1"></i>
                                    Action Initiated
                                  </button>
                                )}
                              {issue.status !== "resolved" && (
                                <button
                                  onClick={() => handleProblemFixed(issue._id)}
                                  className="bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-green-700 transition whitespace-nowrap"
                                >
                                  <i className="fas fa-check-circle mr-1"></i>
                                  Problem Fixed
                                </button>
                              )}
                              {issue.status === "resolved" && (
                                <div className="flex items-center justify-center px-3 py-1.5 bg-green-50 rounded">
                                  <span className="text-xs text-green-700 font-medium">
                                    <i className="fas fa-check-double mr-1"></i>
                                    Resolved
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportedIssues;
